/**
 * AuthContext — React 19 context for Firebase auth state.
 *
 * Provides:
 *   user            — Firebase User | null
 *   isAuthenticated — boolean shorthand
 *   isLoading       — true while the first onIdTokenChanged event fires
 *   login()         — email + password sign-in
 *   loginWithGoogle() — Google popup sign-in
 *   logout()        — sign-out + redirect to /login
 *   refreshUser()   — force token refresh (after claims update, etc.)
 *
 * The `onIdTokenChanged` listener covers:
 *   - initial load (null until resolved)
 *   - sign-in / sign-out
 *   - token refresh (every ~55 min)
 *   - page reload
 *
 * Note: tRPC client reads the ID token lazily via getIdToken() from firebase.ts,
 * so it always picks up the current user's token without needing a re-render.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useTransition,
  useCallback,
  type ReactNode,
} from 'react';
import type { User } from 'firebase/auth';
import {
  auth,
  onIdTokenChanged,
  firebaseSignInEmail,
  firebaseSignInGoogle,
  firebaseSignOut,
} from '@/lib/firebase';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

// ── Context ───────────────────────────────────────────────────────────────────
// Exported so stories can inject a mock value via AuthContext.Provider directly.
export const AuthContext = createContext<AuthContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, startTransition] = useTransition();

  // Subscribe to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, (firebaseUser) => {
      startTransition(() => {
        setUser(firebaseUser);
        setIsLoading(false);
      });
    });

    return unsubscribe;
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    await firebaseSignInEmail(email, password);
    // onIdTokenChanged will update state
  }, []);

  const loginWithGoogle = useCallback(async () => {
    await firebaseSignInGoogle();
  }, []);

  const logout = useCallback(async () => {
    await firebaseSignOut();
    // onIdTokenChanged fires with null, state updates automatically
  }, []);

  const refreshUser = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      await currentUser.getIdToken(true);
      setUser({ ...currentUser });
    }
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    login,
    loginWithGoogle,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('[useAuth] must be used within <AuthProvider>');
  }
  return ctx;
}
